import React, { useState } from 'react';
import { Menu, Dropdown, Space, Image, Button, Drawer, Spin } from 'antd';
import { Link, NavLink } from 'react-router-dom';
import { DownOutlined, MenuOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useLocales } from '../../hooks/useFaqCache';
import styles from './Header.module.css';

// Flat bayrak emojileri
const FLAGS = {
  en: '🇬🇧',
  tr: '🇹🇷',
  ru: '🇷🇺'
};

const Header = () => {
  const { t, i18n } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Desteklenen dilleri API'den al
  const { data: localesData, isLoading: localesLoading } = useLocales();
  const locales = localesData?.data || [];

  // Browser dilini kontrol et ve gerekirse ayarla
  React.useEffect(() => {
    const browserLang = navigator.language || navigator.userLanguage;
    const currentLang = i18n.language;
    
    // Eğer henüz bir dil seçilmemişse veya localStorage'da kayıtlı değilse
    if (!currentLang || currentLang === 'en' && browserLang !== 'en') {
      // Browser dilini desteklenen dillerden birine eşleştir
      let detectedLang = 'en';
      if (browserLang.startsWith('tr')) detectedLang = 'tr';
      else if (browserLang.startsWith('ru')) detectedLang = 'ru';
      
      // Eğer algılanan dil mevcut dilden farklıysa değiştir
      if (detectedLang !== currentLang) {
        i18n.changeLanguage(detectedLang);
      }
    }
  }, [i18n]);

  // Dinamik dil menüsü oluştur
  const languageMenu = (
    <Menu
      onClick={({ key }) => i18n.changeLanguage(key)}
      items={locales.map(locale => ({
        key: locale.code,
        label: (
          <Space>
            {FLAGS[locale.code] || '🌐'} {locale.name}
          </Space>
        ),
        disabled: !locale.hasContent, // İçeriği olmayan dilleri devre dışı bırak
      }))}
    />
  );

  const mobileMenuItems = [
    { key: '/', label: t('helpCenter') },
    { key: '/faq', label: t('faq') },
    { key: '/partner', label: t('portal') },
    { key: '/contact', label: t('contact') },
  ];

  const handleMobileMenuClick = ({ key }) => {
    setMobileMenuOpen(false);
    window.location.href = key;
  };

  // Mevcut dili bul
  const currentLocale = locales.find(locale => locale.code === i18n.language) || locales[0];

  return (
    <>
      <header className={styles.header}>
        <Link to="/" className={styles.logo}>
          <Image 
            preview={false} 
            src="https://tourzone.com/Assets/img/tzstickylogo.svg" 
            alt="Tourzone Logo" 
            height={20}
            style={{ display: 'inline' }}
          />
        </Link>

        <nav className={styles.nav}>
          <NavLink to="/" className={({ isActive }) => (isActive ? styles.active : undefined)}>
            {t('helpCenter')}
          </NavLink>
          <NavLink to="/faq" className={({ isActive }) => (isActive ? styles.active : undefined)}>
            {t('faq')}
          </NavLink>
          <NavLink to="/partner" className={({ isActive }) => (isActive ? styles.active : undefined)}>
            {t('portal')}
          </NavLink>
          <NavLink to="/contact" className={({ isActive }) => (isActive ? styles.active : undefined)}>
            {t('contact')}
          </NavLink>
        </nav>

        <div className={styles.headerRight}>
          <div className={styles.lang}>
            <Dropdown overlay={languageMenu} disabled={localesLoading}>
              <Button type="text" className={styles.langButton}>
                <Space>
                  {localesLoading ? (
                    <Spin size="small" />
                  ) : (
                    <>
                      {FLAGS[currentLocale?.code] || '🌐'} 
                      {currentLocale?.name || 'Loading...'}
                      <DownOutlined />
                    </>
                  )}
                </Space>
              </Button>
            </Dropdown>
          </div>

          <Button
            type="text"
            className={styles.hamburgerMenu}
            icon={<MenuOutlined />}
            onClick={() => setMobileMenuOpen(true)}
          />
        </div>
      </header>

      <Drawer
        title={t('menu')}
        placement="right"
        onClose={() => setMobileMenuOpen(false)}
        open={mobileMenuOpen}
        className={styles.mobileDrawer}
      >
        <Menu
          mode="vertical"
          items={mobileMenuItems}
          onClick={handleMobileMenuClick}
          className={styles.mobileMenu}
        />
        
        <div className={styles.mobileLanguage}>
          <h4>{t('language')}</h4>
          <Menu
            mode="vertical"
            items={locales.map(locale => ({
              key: locale.code,
              label: (
                <Space>
                  {FLAGS[locale.code] || '🌐'} {locale.name}
                </Space>
              ),
              disabled: !locale.hasContent,
            }))}
            onClick={({ key }) => {
              i18n.changeLanguage(key);
              setMobileMenuOpen(false);
            }}
          />
        </div>
      </Drawer>
    </>
  );
};

export default Header;
